import { PropsWithChildren, useMemo } from 'react'
import { PulseFilters } from 'api/pulse'
import { usePulseFilterQueryState } from '../hooks/usePulseFilterQueryState'
import { createContext } from 'lib/createContext'
import { usePulsePositionTypes } from 'queries/pulse/usePulsePositionTypes'
import { useAuthContext } from 'components/auth'
import { getCompaniesInCompanyGroup } from 'lib/companies'

type PulseProviderState = {
  state: {
    filters: PulseFilters
    isPositionTypesLoading: boolean
    companies: { value: number; label: string }[]
    positionTypes: { value: string; label: string }[]
  }
  actions: {
    setFilters: (filters: PulseFilters) => void
  }
}

export const [PulseContext, usePulseContext] =
  createContext<PulseProviderState>({
    name: 'PulseContext',
  })

export function PulseProvider({ children }: PropsWithChildren) {
  const { company } = useAuthContext()

  const companiesOptions = useMemo(() => {
    if (!company) {
      return []
    }

    const companies = getCompaniesInCompanyGroup(company)

    return companies
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((c) => ({
        value: c.id,
        label: c.name,
      }))
  }, [company])

  const [filters, setFilters] = usePulseFilterQueryState(
    companiesOptions?.map((c) => c.value) ?? []
  )

  const { data: positionTypes, isLoading: isPositionTypesLoading } =
    usePulsePositionTypes(filters)

  const positionTypesOptions = useMemo(() => {
    return (positionTypes?.position_types ?? []).map((p) => ({
      value: p,
      label: p,
    }))
  }, [positionTypes])

  const value = useMemo(() => {
    return {
      state: {
        filters,
        isPositionTypesLoading,
        companies: companiesOptions,
        positionTypes: positionTypesOptions,
        timePeriod: filters.timePeriod,
      },
      actions: {
        setFilters,
      },
    } as PulseProviderState
  }, [
    filters,
    companiesOptions,
    positionTypesOptions,
    positionTypes,
    isPositionTypesLoading,
  ])

  return <PulseContext.Provider value={value}>{children}</PulseContext.Provider>
}
